'use client'

import type { CitiesExploreBlock as CitiesExploreBlockProps } from 'src/payload-types'
import { cn } from '@/utilities/ui'
import React, { useState, useEffect } from 'react'
import Slider from 'react-slick'
import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'
import Link from 'next/link'
import Image from 'next/image'

type City = {
  name: string
  ar_name: string
  propertyCount: number
  image: string
  link: string
}
type Props = {
  className?: string
  dictionary: any
  lang?: string,
  cities: City[]
} & CitiesExploreBlockProps

export const CitiesExploreBlock = ({ 
  className,
  cities, 
  dictionary, 
  lang 
}: Props) => {

  const [isMobile, setIsMobile] = useState(false)

  // Check if we're on mobile
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }

    // Initial check
    checkIfMobile()

    // Add event listener
    window.addEventListener('resize', checkIfMobile)

    // Cleanup
    return () => {
      window.removeEventListener('resize', checkIfMobile)
    }
  }, [])

  // Settings for React Slick carousel - showing 1.5 cards on mobile
  const sliderSettings = {
    dots: false,
    infinite: false,
    speed: 500,
    slidesToShow: 2,
    slidesToScroll: 1,
    arrows: false,
    centerMode: false,
    variableWidth: false,
    cssEase: 'ease-out',
    responsive: [
      {
        breakpoint: 512,
        settings: {
          slidesToShow: 1.5,
        },
      },
    ],
  }

  // City Card Component (reused in both layouts)
  const CityCard = ({ city, className }: { city: City; className?: string }) => {

    return (
      <div className={cn('relative rounded-lg overflow-hidden group h-full', className)}>
        {/* City Image */}
        <div className="absolute inset-0">
          <Image
            src={city.image}
            alt={city.name || 'City image'}
            fill
            className="object-cover transition-transform duration-300 group-hover:scale-105"
            sizes="(max-width: 768px) 100vw, 25vw"
            priority={false}
          />
        </div>

        {/* Overlay */}
        <div className="absolute inset-0 bg-black bg-opacity-40 transition-opacity group-hover:bg-opacity-20" />

        {/* City info */}
        <div className="absolute top-0 left-0 p-4 text-white">
          <h3 className="text-h5 font-medium mb-1">
            {lang === 'ar' ? city.ar_name || 'N/A' : city.name || 'N/A'}
          </h3>
          <p className="text-body opacity-90">{city.propertyCount ? city.propertyCount + ' ' + dictionary?.propertyDetail?.agencyList?.properties : dictionary?.propertyDetail?.homePageListing?.comingSoon}</p>
        </div>

        {/* Link overlay */}
        <Link
          href={lang ? `/${lang}${city.link}` : city.link}
          className="absolute inset-0"
          aria-label={`Explore properties in ${city.name || 'N/A'}`}
        />
      </div>
    )
  }
  return (
    <div className={cn('pt-5 bg-light', className)}>
      <div className="container mx-auto max-sm:px-4">
        <div className="flex items-center mb-5">
          {isMobile ? (
            <Link
              href={lang ? `/${lang}/cities` : '/cities'}
              className="flex items-center font-semibold text-mobile-h2 text-typography hover:underline"
            >
              {dictionary?.citiesExplore?.exploreCities || 'See All Cities'}
              <svg
                className="ml-2 w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M14 5l7 7m0 0l-7 7m7-7H3"
                />
              </svg>
            </Link>
          ) : (
            <h2 className="text-h2 font-semibold text-typography">
              {dictionary?.citiesExplore?.exploreCities || 'Explore Cities'}
            </h2>
          )}
        </div>

        {!isMobile ? (
          /* Desktop Layout - Using a flex-based approach instead of grid */
          <div className="grid grid-cols-4 grid-rows-2 h-[500px] gap-4">
            {cities.map((city, index) => (
              <div 
                key={index} 
              >
                <CityCard city={city} className="h-full" />
              </div>
            ))}
          </div>
        ) : (
          /* Mobile Carousel Layout */
          <div className="mx-4 h-[350px] sm:h-[460px]">
            <Slider {...sliderSettings} className="cities-carousel -mx-1 px-1 h-full">
              {cities.map((city, index) => (
                <div key={index} className="px-2 h-full" style={{ width: '100%' }}>
                  <CityCard city={city} className="h-full" />
                </div>
              ))}
            </Slider>
          </div>
        )}
      </div>
    </div>
  )
}
